/**
 * EnablePushBanner — Iter-245
 *
 * Dashboard-level enrollment banner that prompts users to opt into
 * Web Push notifications. Renders ONLY when:
 *   • The browser supports push (Service Worker + Push API)
 *   • The current permission state is 'default' (never asked)
 *   • The user has not previously dismissed the banner (localStorage)
 *
 * Three CTAs:
 *   • Enable Push (one-click)  — requests permission + subscribes
 *   • Send Test                 — visible after enable; fires the
 *                                  backend test endpoint
 *   • Dismiss                   — hides for 30 days
 *
 * Drop this once on each role's home/dashboard page. The banner is
 * SSR-safe and never throws even if push fails.
 */
import React, { useEffect, useState } from 'react';
import { BellRing, X, CheckCircle, Loader2, Send } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { pushClient } from '../utils/pushClient';

const DISMISS_KEY = 'enable_push_banner_dismissed_until';

const EnablePushBanner = () => {
  const [visible, setVisible] = useState(false);
  const [working, setWorking] = useState(false);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!pushClient.isSupported()) return;
        const perm = pushClient.permission();
        const alreadyEnabled = await pushClient.isEnabled().catch(() => false);
        const dismissedUntil = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
        const stillDismissed = dismissedUntil && dismissedUntil > Date.now();
        if (cancelled) return;
        // Show if permission is default (never asked) AND not dismissed.
        if (perm === 'default' && !stillDismissed) {
          setVisible(true);
        }
        // Show as "enabled" state if already opted in (so the test
        // button is reachable from the dashboard).
        if (alreadyEnabled && perm === 'granted' && !stillDismissed) {
          setVisible(true);
          setEnabled(true);
        }
      } catch {
        // never break the dashboard for a banner.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleEnable = async () => {
    setWorking(true);
    try {
      await pushClient.enable();
      setEnabled(true);
      toast.success('Push notifications enabled. Tap Send Test to confirm.');
    } catch (e) {
      toast.error(e?.message || 'Could not enable push. Check your browser permissions.');
    } finally {
      setWorking(false);
    }
  };

  const handleSendTest = async () => {
    setWorking(true);
    try {
      await pushClient.sendTest();
      toast.success('Test push sent — check your device.');
    } catch {
      toast.error('Test push failed. Tap Enable Push again to refresh the subscription.');
    } finally {
      setWorking(false);
    }
  };

  const handleDismiss = () => {
    // Snooze for 30 days; per-device.
    localStorage.setItem(DISMISS_KEY, String(Date.now() + 30 * 24 * 60 * 60 * 1000));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="mb-4 rounded-lg border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 px-4 py-3 shadow-sm"
      data-testid="enable-push-banner"
      role="region"
      aria-label="Enable push notifications"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-grow min-w-0">
          <div className="rounded-full bg-amber-100 p-2 mt-0.5">
            <BellRing className="w-4 h-4 text-amber-700" />
          </div>
          <div className="flex-grow min-w-0">
            <p className="text-sm font-semibold text-amber-900">
              {enabled
                ? 'Push notifications are on. Want to confirm with a test?'
                : 'Turn on push notifications'}
            </p>
            <p className="text-xs text-amber-800 mt-0.5">
              {enabled
                ? 'Tap Send Test to fire a sample notification to this device.'
                : 'Get real-time alerts for messages, new applications, booking updates, and uploads — even when this tab is closed.'}
            </p>
            <div className="flex flex-wrap gap-2 mt-2">
              {!enabled && (
                <Button
                  onClick={handleEnable}
                  disabled={working}
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="enable-push-banner-enable-btn"
                >
                  {working ? (
                    <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Enabling…</>
                  ) : (
                    <><CheckCircle className="w-3.5 h-3.5 mr-1.5" />Enable Push</>
                  )}
                </Button>
              )}
              <Button
                onClick={handleSendTest}
                disabled={working || !enabled}
                size="sm"
                variant={enabled ? 'default' : 'outline'}
                className={enabled
                  ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                  : 'border-amber-300 text-amber-800 hover:bg-amber-100'}
                data-testid="enable-push-banner-test-btn"
                title={enabled ? 'Send a test push to this device' : 'Enable push first'}
              >
                <Send className="w-3.5 h-3.5 mr-1.5" />
                Send Test
              </Button>
              <Button
                onClick={handleDismiss}
                variant="ghost"
                size="sm"
                className="text-amber-700 hover:bg-amber-100"
                data-testid="enable-push-banner-dismiss-btn"
              >
                Remind me later
              </Button>
            </div>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="text-amber-500 hover:text-amber-700 -mt-1 -mr-1 p-1"
          data-testid="enable-push-banner-close-btn"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default EnablePushBanner;
